import type { SubmitHandler } from 'react-hook-form';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateAppSettingsResponseBody } from '~/lib/api/modules/AppSettings';
import type { IUseUpdateAppSettingsMutationValues } from '~/lib/api/modules/AppSettings/hooks/updateAppSettingsMutation';
import type { ILookAndFeelSettingsFormValues } from './LookAndFeelSettingsForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppPreviewState } from '~/components/organisms/AppPreview';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppSettingsMutation } from '~/lib/api/modules/AppSettings/hooks/updateAppSettingsMutation';
import { LookAndFeelSettingsForm } from './LookAndFeelSettingsForm';

export type IUpdateLookAndFeelSettingsFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateAppSettingsResponseBody,
    inputValues: IUseUpdateAppSettingsMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
};

export const UpdateLookAndFeelSettingsForm: React.FC<
  IUpdateLookAndFeelSettingsFormProps
> = (props) => {
  const { sx, onSuccess, cancelUrl, appId } = props;
  const updateAppSettingsMutation = useUpdateAppSettingsMutation();
  const appPreviewState = useAppPreviewState();
  const appQuery = useAppQuery({ appId });
  const appSettings = appQuery.data?.settings;

  const handleSubmit: SubmitHandler<ILookAndFeelSettingsFormValues> = (
    values,
  ) => {
    if (updateAppSettingsMutation.isLoading || !appSettings) {
      return;
    }

    return updateAppSettingsMutation.mutateAsync(
      {
        appId,
        body: {
          ...appSettings,
          press: {
            ...appSettings.press,
            env: {
              ...appSettings.press?.env,
              appThemeColorPrimary: values.appThemeColorPrimary,
            },
          },
        },
      },
      {
        onSuccess: (outputData, inputValues) => {
          onSuccess?.(outputData, inputValues);
          appPreviewState.iframeRef?.current?.contentWindow?.postMessage(
            {
              action: 'refreshAppSettings',
              crowdaa: true,
            },
            '*',
          );
        },
      },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Suspense
        loading={appQuery.isLoading}
        error={appQuery.error}
        first
      >
        {appSettings ? (
          <LookAndFeelSettingsForm
            first
            mode="edition"
            defaultValues={{
              appThemeColorPrimary:
                appSettings.press?.env?.appThemeColorPrimary,
            }}
            cancelUrl={cancelUrl}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
